import React from "react";
import { Plane, Hotel, Clock, ArrowRight, Zap, BedDouble, Star } from "lucide-react";

/**
 * Renders the two live flight options + two live hotel options pulled from
 * Amadeus and attached to the budget object as `flight_options` and
 * `hotel_options`. Designed to sit immediately below the BudgetTable so the
 * guest sees real schedules + room categories without Sasha hallucinating them.
 */

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // e.g., "Apr 12 · 18:30"
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = d.getDate();
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${month} ${day} · ${time}`;
  } catch {
    return iso;
  }
}

function fmtIsoDuration(iso) {
  // PT7H30M -> "7h 30m"
  if (!iso || typeof iso !== "string") return "—";
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return iso;
  const h = m[1] ? `${m[1]}h` : "";
  const min = m[2] ? `${m[2]}m` : "";
  return [h, min].filter(Boolean).join(" ") || "—";
}

function fmtUsd(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function FlightLeg({ leg, label }) {
  if (!leg) return null;
  return (
    <div className="border-l-2 border-[var(--gold)]/40 pl-3 py-1">
      <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--brand-red)]">
        {label}
      </div>
      <div className="flex items-center gap-2 text-sm text-[var(--ink)] mt-1 tabular-nums">
        <span className="font-medium">{leg.from || "—"}</span>
        <ArrowRight size={12} strokeWidth={1.5} className="text-[var(--muted)]" />
        <span className="font-medium">{leg.to || "—"}</span>
      </div>
      <div className="text-[11px] text-[var(--ink-2)] mt-0.5 tabular-nums">
        {fmtDateTime(leg.departure_at)} → {fmtDateTime(leg.arrival_at)}
      </div>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[var(--muted)] mt-1">
        <Clock size={10} strokeWidth={1.5} />
        <span>{fmtIsoDuration(leg.duration)}</span>
        <span>·</span>
        <span>{leg.stops === 0 ? "Nonstop" : `${leg.stops} stop${leg.stops > 1 ? "s" : ""}`}</span>
      </div>
    </div>
  );
}

function FlightCard({ option, idx }) {
  return (
    <div
      className="lux-card !p-5 border-t-2 border-[var(--gold)] flex flex-col gap-3"
      data-testid={`flight-option-${idx}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Plane size={14} strokeWidth={1.5} className="text-[var(--brand-red)]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-red)]">
              Option {idx + 1}
            </span>
            <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.14em] text-[var(--emerald)]">
              <Zap size={9} strokeWidth={2} /> live
            </span>
          </div>
          <div className="font-display text-2xl text-[var(--emerald)] mt-1 leading-tight">
            {option.carrier_name || option.carrier_code}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-2)] mt-1">
            {option.flight_number} · {option.cabin || "Economy"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl text-[var(--emerald)] tabular-nums">
            {fmtUsd(option.price_usd)}
          </div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">
            round-trip · all travelers
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-1">
        <FlightLeg leg={option.outbound} label="Outbound" />
        <FlightLeg leg={option.return} label="Return" />
      </div>
    </div>
  );
}

function HotelCard({ option, idx }) {
  return (
    <div
      className="lux-card !p-5 border-t-2 border-[var(--gold)] flex flex-col gap-3"
      data-testid={`hotel-option-${idx}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Hotel size={14} strokeWidth={1.5} className="text-[var(--brand-red)]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-red)]">
              Stay {idx + 1}
            </span>
            <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.14em] text-[var(--emerald)]">
              <Zap size={9} strokeWidth={2} /> live
            </span>
          </div>
          <div className="font-display text-2xl text-[var(--emerald)] mt-1 leading-tight">
            {option.hotel_name}
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            {option.rating > 0 &&
              Array.from({ length: option.rating }).map((_, i) => (
                <Star
                  key={`star-${i}`}
                  size={10}
                  strokeWidth={1.5}
                  className="text-[var(--gold)] fill-[var(--gold)]"
                />
              ))}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl text-[var(--emerald)] tabular-nums">
            {fmtUsd(option.total_usd)}
          </div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">
            {option.nights} {option.nights === 1 ? "night" : "nights"} · {fmtUsd(option.nightly_usd)}/nt
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-[11px] text-[var(--ink-2)]">
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--brand-red)]">Check-in</div>
          <div className="mt-1 tabular-nums">{option.check_in || "—"}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--brand-red)]">Check-out</div>
          <div className="mt-1 tabular-nums">{option.check_out || "—"}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">
        <BedDouble size={11} strokeWidth={1.5} />
        <span>{option.room_type || "Standard room"}</span>
        <span>·</span>
        <span>{option.board || "Room Only"}</span>
        {option.refundable && <span className="text-[var(--emerald)]">· Refundable</span>}
      </div>
    </div>
  );
}

export default function LiveOptionsPanel({ budget }) {
  if (!budget) return null;
  const flights = Array.isArray(budget.flight_options) ? budget.flight_options : [];
  const hotels = Array.isArray(budget.hotel_options) ? budget.hotel_options : [];
  if (!flights.length && !hotels.length) return null;

  return (
    <div className="mt-4 max-w-3xl space-y-5" data-testid="live-options-panel">
      {flights.length > 0 && (
        <section>
          <div className="overline mb-2">Live flight options</div>
          <div className="grid gap-3">
            {flights.map((f, i) => (
              <FlightCard key={`flight-${i}`} option={f} idx={i} />
            ))}
          </div>
        </section>
      )}
      {hotels.length > 0 && (
        <section>
          <div className="overline mb-2">Live hotel options</div>
          <div className="grid gap-3">
            {hotels.map((h, i) => (
              <HotelCard key={`hotel-${i}`} option={h} idx={i} />
            ))}
          </div>
        </section>
      )}
      <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mt-1">
        Pricing pulled live from Janté preferred-partner inventory · subject to change at booking time
      </p>
    </div>
  );
}
